import React, {
  ReactNode,
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
} from "react";
import { ColorResult, CustomPicker } from "react-color";
import {
  Saturation,
  EditableInput,
  Hue,
} from "react-color/lib/components/common";
import tinycolor2 from "tinycolor2";

import { CssProperties } from "@/states/page-editor/template-parts/common";

interface Props {
  color: string;
  onChange: (color: ColorResult) => void;
}
interface ColorState {
  hsl: { h: number; s: number; l: number };
  hsv: { h: number; s: number; v: number };
  rgb: { r: number; g: number; b: number };
  hex: string;
}

const CustomColorPickerSP: React.FC<Props> = ({ color, onChange }) => {
  const inputStyles = {
    input: {
      width: "100%",
      border: "1px solid #A1A6AC",
      borderRadius: "4px",
      padding: "12px 8px",
      color: "#222",
    },
  };
  const pointerStyles: CssProperties = {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    borderWidth: "3px",
    borderColor: "rgb(248, 248, 248)",
    boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.15)",
    boxSizing: "border-box",
    transform: "translate(-8px,-8px)",
  };

  const CustomSlider = () => {
    return (
      <div
        id="custom-slider"
        style={{ ...pointerStyles, transform: "translate(-3px,-8px)" }}
      />
    );
  };

  const CustomPointer = (): ReactNode => {
    return <div id="custom-pointer" style={pointerStyles} />;
  };

  const hexToColorState = (hexColor: string): ColorState => {
    const color: tinycolor2.Instance = tinycolor2(hexColor);
    return {
      hsl: color.toHsl(),
      hsv: color.toHsv(),
      rgb: color.toRgb(),
      hex: color.toHex(),
    };
  };
  const [currentColor, setCurrentColor] = useState<ColorState>(
    hexToColorState(color),
  );
  useEffect(() => {
    setCurrentColor(hexToColorState(color));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  useEffect(() => {
    onChange(currentColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentColor]);

  const handleSaturationChange = (newColor: unknown) => {
    const color: tinycolor2.Instance = tinycolor2(
      newColor as { h: number; s: number; v: number },
    );
    setCurrentColor(hexToColorState(color.toHex()));
  };

  const handleHueChange = (newColor: unknown) => {
    const color: tinycolor2.Instance = tinycolor2(
      newColor as { h: number; s: number; l: number },
    );
    setCurrentColor(hexToColorState(color.toHex()));
  };

  const handleHexChange = (newColor: unknown) => {
    const color: tinycolor2.Instance = tinycolor2(newColor as string);
    setCurrentColor(hexToColorState(color.toHex()));
  };

  const handleRgbChange = useCallback(
    (val: string, rgb: "r" | "g" | "b") => {
      const num = Number(val);
      if (isNaN(num)) {
        return;
      }
      const color: tinycolor2.Instance = tinycolor2({
        ...currentColor.rgb,
        [rgb]: num,
      });
      setCurrentColor(hexToColorState(color.toHex()));
    },
    [currentColor],
  );

  return (
    <>
      <div className="flex flex-wrap justify-between w-full">
        <div className="relative w-[279px] h-[279px] sm:w-[222px] sm:h-full">
          <Saturation
            {...currentColor}
            pointer={CustomPointer as unknown as ReactNode}
            onChange={handleSaturationChange}
          />
        </div>
        <div className="relative w-[10px] h-[279px] sm:h-full md:mx-[19px]">
          <Hue
            {...currentColor}
            pointer={CustomSlider as unknown as ReactNode}
            onChange={handleHueChange}
            direction="vertical"
            className="overflow-hidden rounded-full"
          />
        </div>
      </div>
      <fieldset className="mt-4 text-theme">
        <div className="grid grid-cols-3 gap-2">
          <div className="flex justify-between items-center space-x-2">
            <label>R</label>
            <input
              type="text"
              style={inputStyles.input}
              value={currentColor.rgb.r}
              onChange={(e) => handleRgbChange(e.target.value, "r")}
            />
          </div>
          <div className="flex justify-between items-center space-x-2">
            <label>G</label>
            <input
              type="text"
              style={inputStyles.input}
              value={currentColor.rgb.g}
              onChange={(e) => handleRgbChange(e.target.value, "g")}
            />
          </div>
          <div className="flex justify-between items-center space-x-2">
            <label>B</label>
            <input
              type="text"
              style={inputStyles.input}
              value={currentColor.rgb.b}
              onChange={(e) => handleRgbChange(e.target.value, "b")}
            />
          </div>
        </div>
        <div className="grid grid-cols-[auto_91px] gap-4 mt-4">
          <div className="flex justify-between items-center space-x-2">
            <label>#</label>
            <EditableInput
              style={inputStyles}
              value={currentColor.hex}
              onChange={handleHexChange}
            />
          </div>
          <div
            className="w-full h-12"
            style={{ backgroundColor: "#" + currentColor.hex }}
          />
        </div>
      </fieldset>
    </>
  );
};

export default CustomPicker(
  CustomColorPickerSP as unknown as FunctionComponent,
);
